import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertCircle, UserCheck, Clock, FileWarning } from "lucide-react";
import { EmailSetupCard } from "@/components/admin/EmailSetupCard";
import { EmailHealthBanner } from "@/components/admin/EmailHealthBanner";
import { setViewAs } from "@/lib/viewAs";
import { useNavigate } from "@tanstack/react-router";
import { Eye } from "lucide-react";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Command Center — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminPortal,
});

function AdminPortal() {
  const location = useLocation();
  const isOverview = location.pathname === "/admin";

  return (
    <ProtectedRoute requiredRole="admin">
      {isOverview ? (
        <PortalShell title="Command Center" nav={ADMIN_NAV} tourKey="admin_tour" headerVariant="charcoal">
          <EmailHealthBanner />
          <EmailSetupCard />
          <PreviewPortalsCard />
          <Overview />
        </PortalShell>
      ) : (
        <Outlet />
      )}
    </ProtectedRoute>
  );
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

function PreviewPortalsCard() {
  const navigate = useNavigate();
  const enter = async (role: "client" | "agent") => {
    await setViewAs(role);
    navigate({ to: role === "client" ? "/client" : "/agent" });
  };
  return (
    <div className="mb-6 border border-bronze-600/30 bg-white p-5 shadow-soft rounded-[4px]">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
            Support Tools
          </p>
          <h3 className="mt-1 font-display text-[20px] text-navy-900">Preview a portal as the user sees it</h3>
          <p className="mt-1 font-sans text-[13px] text-charcoal-500">
            Enter read-only preview mode to troubleshoot what Clients or Strategists are experiencing. Writes are disabled while previewing.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => void enter("client")}
            className="inline-flex items-center gap-2 border border-bronze-600 bg-cream-50 px-4 py-2 font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-navy-800 hover:bg-bronze-400/20"
          >
            <Eye className="h-3.5 w-3.5" /> View as Client
          </button>
          <button
            onClick={() => void enter("agent")}
            className="inline-flex items-center gap-2 border border-bronze-600 bg-cream-50 px-4 py-2 font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-navy-800 hover:bg-bronze-400/20"
          >
            <Eye className="h-3.5 w-3.5" /> View as Strategist
          </button>
        </div>
      </div>
    </div>
  );
}

function StatSeal({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-bronze-600/20 rounded-[4px] p-3 sm:p-5 shadow-soft flex flex-col items-center text-center min-w-0">
      <p className="font-sans text-[9px] font-semibold uppercase tracking-[0.22em] text-bronze-600 truncate max-w-full">{label}</p>
      <p className="mt-2 font-display font-semibold text-navy-800 text-[18px] sm:text-[24px] leading-none truncate max-w-full">{value}</p>
    </div>
  );
}

interface OverviewState {
  totalPolicies: number;
  mtdProduction: number;
  activeClients: number;
  activeAgents: number;
  pendingOnboarding: number;
  openLeads: number;
  conversionRate: number;
  monthlyRevenue: number;
  series: Array<{ month: string; value: number }>;
  sources: Array<{ name: string; value: number }>;
  leaderboard: Array<{ name: string; production: number }>;
  pendingAgents: number;
  staleLeads: number;
  expiringLicenses: number;
  unassignedClients: number;
}

function Overview() {
  const [s, setS] = useState<OverviewState | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString();
    const sixtyDaysAhead = new Date(Date.now() + 60 * 86400000).toISOString().slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);

    const [polRes, polCount, clientsCount, agentsRes, onboardCount, leadsAll, sourcesRes, agentsLb, profilesRes, licensesRes, unassignedRes] =
      await Promise.all([
        supabase.from("policies").select("face_amount, effective_date, premium, agent_id").gte("effective_date", yearStart),
        supabase.from("policies").select("id", { count: "exact", head: true }),
        supabase.from("clients").select("id", { count: "exact", head: true }),
        supabase.from("agents").select("user_id, status"),
        supabase.from("clients").select("id", { count: "exact", head: true }).neq("onboarding_status", "complete"),
        supabase.from("leads").select("stage, source, updated_at"),
        supabase.from("leads").select("source"),
        supabase.from("policies").select("agent_id, face_amount").gte("effective_date", yearStart),
        supabase.from("profiles").select("id, full_name"),
        supabase.from("state_licenses").select("id", { count: "exact", head: true })
          .eq("active", true).gte("expiration_date", today).lte("expiration_date", sixtyDaysAhead),
        supabase.from("clients").select("id", { count: "exact", head: true }).is("assigned_agent_id", null),
      ]);

    const policies = (polRes.data ?? []) as Array<{ face_amount: number | null; effective_date: string | null; premium: number | null; agent_id: string | null }>;
    const mtdProduction = policies
      .filter((p) => p.effective_date && p.effective_date >= monthStart)
      .reduce((acc, p) => acc + (Number(p.face_amount) || 0), 0);
    const monthlyRevenue = policies
      .filter((p) => p.effective_date && p.effective_date >= monthStart)
      .reduce((acc, p) => acc + (Number(p.premium) || 0), 0);

    const series: Array<{ month: string; value: number }> = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      series.push({
        month: d.toLocaleDateString("en-US", { month: "short" }),
        value: policies.filter((p) => p.effective_date?.startsWith(key)).reduce((s2, p) => s2 + (Number(p.face_amount) || 0), 0),
      });
    }

    const leads = (leadsAll.data ?? []) as Array<{ stage: string; source: string | null; updated_at: string }>;
    const open = leads.filter((l) => l.stage !== "converted" && l.stage !== "lost").length;
    const converted = leads.filter((l) => l.stage === "converted").length;
    const conversionRate = leads.length > 0 ? (converted / leads.length) * 100 : 0;
    const staleLeads = leads.filter((l) => l.stage !== "converted" && l.stage !== "lost" && l.updated_at < sevenDaysAgo).length;

    const sourceMap = new Map<string, number>();
    for (const l of (sourcesRes.data ?? []) as Array<{ source: string | null }>) {
      const key = l.source || "Direct";
      sourceMap.set(key, (sourceMap.get(key) ?? 0) + 1);
    }
    const sources = Array.from(sourceMap.entries()).map(([name, value]) => ({ name, value }));

    const agents = (agentsRes.data ?? []) as Array<{ user_id: string; status: string }>;
    const activeAgents = agents.filter((a) => a.status === "active").length;
    const pendingAgents = agents.filter((a) => a.status === "pending").length;

    // Leaderboard: aggregate YTD production per agent
    const prodMap = new Map<string, number>();
    for (const p of (agentsLb.data ?? []) as Array<{ agent_id: string | null; face_amount: number | null }>) {
      if (!p.agent_id) continue;
      prodMap.set(p.agent_id, (prodMap.get(p.agent_id) ?? 0) + (Number(p.face_amount) || 0));
    }
    const profileMap = new Map<string, string>();
    for (const pr of (profilesRes.data ?? []) as Array<{ id: string; full_name: string | null }>) {
      profileMap.set(pr.id, pr.full_name || "Unnamed Strategist");
    }
    const leaderboard = Array.from(prodMap.entries())
      .map(([id, production]) => ({ name: profileMap.get(id) || "Strategist", production }))
      .sort((a, b) => b.production - a.production)
      .slice(0, 5);

    setS({
      totalPolicies: polCount.count ?? 0,
      mtdProduction,
      activeClients: clientsCount.count ?? 0,
      activeAgents,
      pendingOnboarding: onboardCount.count ?? 0,
      openLeads: open,
      conversionRate,
      monthlyRevenue,
      series,
      sources,
      leaderboard,
      pendingAgents,
      staleLeads,
      expiringLicenses: licensesRes.count ?? 0,
      unassignedClients: unassignedRes.count ?? 0,
    });
  }

  const donutColors = ["#0A3369", "#143D7C", "#A88B5C", "#C9A876", "#6B7280", "#1F2937"];

  return (
    <div className="max-w-7xl mx-auto min-w-0">
      {s && (
        <p className="font-sans italic text-charcoal-500" style={{ fontSize: 13 }}>
          {fmt(s.mtdProduction)} placed this month. {s.activeClients} active clients.{" "}
          {s.pendingAgents} strategist approval{s.pendingAgents === 1 ? "" : "s"} pending.
        </p>
      )}
      <h2 className="font-display font-semibold text-navy-800 text-[26px] sm:text-[36px]" style={{ letterSpacing: "-0.01em" }}>
        Command <em className="text-bronze-600 italic font-normal">Center</em>
      </h2>

      {s && s.unassignedClients > 0 && (
        <div className="mt-5 rounded-[4px] border border-warning/40 bg-warning/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="font-sans text-[13px] text-charcoal-900 flex-1">
            <strong>{s.unassignedClients}</strong> client{s.unassignedClients === 1 ? " has" : "s have"} no
            assigned Strategist. Messaging, appointments, reviews and onboarding stay disabled until one is assigned.
          </p>
          <Link
            to="/admin/clients"
            className="font-sans text-[11px] font-semibold uppercase tracking-[0.12em] text-navy-800 underline decoration-bronze-400 underline-offset-4 shrink-0"
          >
            Assign now
          </Link>
        </div>
      )}

      <div data-tour="admin-kpis" className="mt-6 sm:mt-8 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
        <StatSeal label="Total Policies" value={String(s?.totalPolicies ?? 0)} />
        <StatSeal label="MTD Production" value={fmt(s?.mtdProduction ?? 0)} />
        <StatSeal label="Active Clients" value={String(s?.activeClients ?? 0)} />
        <StatSeal label="Active Agents" value={String(s?.activeAgents ?? 0)} />
        <StatSeal label="Pending Onboarding" value={String(s?.pendingOnboarding ?? 0)} />
        <StatSeal label="Open Leads" value={String(s?.openLeads ?? 0)} />
        <StatSeal label="Conversion Rate" value={`${(s?.conversionRate ?? 0).toFixed(1)}%`} />
        <StatSeal label="Monthly Revenue" value={fmt(s?.monthlyRevenue ?? 0)} />
      </div>

      <div className="mt-6 sm:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white border border-bronze-600/20 rounded-[4px] p-4 sm:p-6 shadow-soft min-w-0">
          <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
            Production · Last 12 Months
          </p>
          <div className="mt-4 w-full" style={{ height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={s?.series ?? []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="adminProdFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0A3369" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0A3369" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#A88B5C" strokeOpacity={0.2} strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis
                  tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} width={40}
                  tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)}
                />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #A88B5C", borderRadius: 4, fontSize: 12 }}
                  formatter={(v: number) => [fmt(v), "Production"]}
                />
                <Area type="monotone" dataKey="value" stroke="#A88B5C" strokeWidth={2} fill="url(#adminProdFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-bronze-600/20 rounded-[4px] p-4 sm:p-6 shadow-soft min-w-0">
          <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
            Lead Sources
          </p>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="w-full sm:flex-1" style={{ height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={s?.sources ?? []} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {(s?.sources ?? []).map((_, i) => (
                      <Cell key={i} fill={donutColors[i % donutColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #A88B5C", borderRadius: 4, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="w-full sm:flex-1 space-y-2">
              {(s?.sources ?? []).map((src, i) => (
                <li key={src.name} className="flex items-center gap-2 font-sans text-[12px] text-charcoal-900 min-w-0">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: donutColors[i % donutColors.length] }} />
                  <span className="flex-1 truncate">{src.name}</span>
                  <span className="text-charcoal-500">{src.value}</span>
                </li>
              ))}
              {(s?.sources ?? []).length === 0 && (
                <li className="font-sans text-[12px] text-charcoal-500">No lead source data.</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:mt-8 bg-white border border-bronze-600/20 rounded-[4px] p-4 sm:p-6 shadow-soft min-w-0">
        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
          Top 5 Strategists · YTD Production
        </p>
        <div className="mt-4 w-full" style={{ height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={s?.leaderboard ?? []} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#A88B5C" strokeOpacity={0.2} strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number" tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)}
              />
              <YAxis type="category" dataKey="name" tick={{ fill: "#1F2937", fontSize: 11 }} axisLine={false} tickLine={false} width={90} />
              <Tooltip
                contentStyle={{ background: "#fff", border: "1px solid #A88B5C", borderRadius: 4, fontSize: 12 }}
                formatter={(v: number) => [fmt(v), "Production"]}
              />
              <Bar dataKey="production" fill="#A88B5C" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div data-tour="admin-attention" className="mt-6 sm:mt-8 bg-white rounded-[4px] p-4 sm:p-6 shadow-soft border border-bronze-600/20 border-l-4 border-l-bronze-600">
        <h3 className="font-display font-semibold text-navy-800 text-[18px] sm:text-[20px]">Needs your attention</h3>
        <ul className="mt-4 divide-y divide-bronze-600/15">
          <li className="py-3 flex items-center gap-3">
            <UserCheck size={18} className="text-bronze-600" />
            <Link to="/admin/agents" className="flex-1 min-w-0 font-sans text-[13px] text-charcoal-900 hover:text-navy-800">
              {s?.pendingAgents ?? 0} pending strategist approvals
            </Link>
            <span className="shrink-0 font-sans text-[11px] text-charcoal-500">Review →</span>
          </li>
          <li className="py-3 flex items-center gap-3">
            <Clock size={18} className="text-bronze-600" />
            <Link to="/admin" className="flex-1 min-w-0 font-sans text-[13px] text-charcoal-900 hover:text-navy-800">
              {s?.staleLeads ?? 0} leads with no contact in 7+ days
            </Link>
            <span className="shrink-0 font-sans text-[11px] text-charcoal-500">Review →</span>
          </li>
          <li className="py-3 flex items-center gap-3">
            <FileWarning size={18} className="text-bronze-600" />
            <Link to="/admin" className="flex-1 min-w-0 font-sans text-[13px] text-charcoal-900 hover:text-navy-800">
              {s?.expiringLicenses ?? 0} licenses expiring in 30 days
            </Link>
            <span className="shrink-0 font-sans text-[11px] text-charcoal-500">Review →</span>
          </li>
          {(s?.pendingAgents ?? 0) === 0 && (s?.staleLeads ?? 0) === 0 && (s?.expiringLicenses ?? 0) === 0 && (
            <li className="py-4 flex items-center gap-2 font-sans text-[13px] text-charcoal-500">
              <AlertCircle size={16} className="text-bronze-600" />
              Nothing pressing. The book is in good order.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}